import { FC, useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import filter from "lodash/filter";
import find from "lodash/find";

import Dialog from "../../components/Dialog";
import QrCode from "../../components/QrCode";

import Graphic from "../../assets/graphics/Processing";

import { usePaymentMethod } from "../../contexts/PaymentMethodContext";
import { usePaymentRequest } from "../../contexts/PaymentRequestContext";

import { isMobileDevice } from "../../helpers/is-mobile-device";
import Button from "../../components/Button";

const isProductionLive =
  import.meta.env.VITE_APP_ENV_MODE === "production-live";

// Payment channels that only support deeplink but need QR code workaround on desktop
const DEEPLINK_QR_WORKAROUND_CHANNELS = ["GOPAY"];

/**
 * Helper function to check if a payment channel requires QR code workaround on desktop
 * These channels only support deeplink but we generate QR codes from deeplink URLs
 * so desktop users can scan with their mobile camera to redirect to the app
 * @param channelName - The display name of the payment channel
 * @returns boolean indicating if the channel needs QR code workaround
 */
const requiresQrCodeWorkaround = (channelName: string | undefined): boolean => {
  if (!channelName) return false;
  return DEEPLINK_QR_WORKAROUND_CHANNELS.includes(channelName);
};

/**
 * Helper function to check if QR code workaround should be displayed
 * @param isMobile - Whether the current device is mobile
 * @param channelName - The name of the payment channel
 * @returns boolean indicating if QR code workaround should be displayed
 */
const shouldDisplayQrCode = (
  isMobile: boolean,
  channelName: string | undefined
): boolean => {
  return !isMobile && requiresQrCodeWorkaround(channelName) && isProductionLive;
};

const PaymentRedirect: FC = () => {
  const [error, setError] = useState<ErrorMessage | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  const { clearPaymentChannel, paymentChannel } = usePaymentMethod();
  const { paymentRequest } = usePaymentRequest();
  const { t } = useTranslation("common");

  const qrRef = useRef<HTMLCanvasElement>(null);
  const isMobile = isMobileDevice();

  const handleQrCodeClick = useCallback(() => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  }, [redirectUrl]);

  useEffect(() => {
    if (paymentRequest) {
      const authActions = filter(paymentRequest.actions, {
        action: "AUTH"
      }) as PaymentRequestAuthAction[];

      try {
        if (!authActions) {
          throw "No auth actions found";
        }
        const shouldRedirectToMobileUrl = isMobile;
        const [mobileAuthAction, webAuthAction, deeplinkAuthAction] = [
          find(authActions, { url_type: "MOBILE" })?.url,
          (find(authActions, { url_type: "WEB" }) as PaymentRequestAuthAction)
            ?.url,
          (
            find(authActions, {
              url_type: "DEEPLINK"
            }) as PaymentRequestAuthAction
          )?.url
        ];

        // For GoPay on desktop in non-production-live environments, use deeplink for direct redirect
        const isDesktop = !shouldRedirectToMobileUrl;
        const isNonProductionEnv = !isProductionLive;
        const isGoPayChannel = requiresQrCodeWorkaround(
          paymentChannel?.channel
        );
        const shouldUseDeeplinkForGoPay =
          isDesktop && isNonProductionEnv && isGoPayChannel;

        let finalRedirectUrl: string | undefined;
        if (shouldRedirectToMobileUrl) {
          // Mobile device: prefer mobile URL, fallback to web or deeplink
          finalRedirectUrl =
            mobileAuthAction || webAuthAction || deeplinkAuthAction;
        } else if (shouldUseDeeplinkForGoPay) {
          // GoPay on desktop in non-production: use deeplink for direct redirect
          finalRedirectUrl = deeplinkAuthAction;
        } else {
          // Default desktop behavior: use web URL
          finalRedirectUrl = webAuthAction;
        }

        if (!finalRedirectUrl) {
          // Check if payment channel requires QR code workaround on non-mobile devices
          if (
            shouldDisplayQrCode(
              shouldRedirectToMobileUrl,
              paymentChannel?.channel
            )
          ) {
            // For channels requiring QR workaround on non-mobile, use action URLs for QR code
            const qrRedirectUrl =
              deeplinkAuthAction || webAuthAction || mobileAuthAction;
            if (qrRedirectUrl) {
              setRedirectUrl(qrRedirectUrl);
              return; // Do not throw error for supported channels on non-mobile
            }
          }
          throw "No redirect URL found";
        }
        window.location.href = finalRedirectUrl;
      } catch (error) {
        setError({
          body: `There was an error while redirecting you to the ${
            paymentChannel?.display_name || "partner"
          } checkout page.`,
          title: "Error"
        });
      }
    }
  }, [
    paymentChannel,
    paymentRequest,
    shouldDisplayQrCode,
    setRedirectUrl,
    setError,
    isMobile,
    isProductionLive,
    requiresQrCodeWorkaround
  ]);

  if (!paymentChannel) {
    return null;
  }

  // Show QR code workaround for supported channels on desktop
  if (shouldDisplayQrCode(isMobile, paymentChannel?.channel)) {
    return (
      <div className="flex flex-col items-center space-y-8 py-12">
        <div
          className="flex flex-col w-80"
          data-testid={`${paymentChannel.label.toLowerCase()}-redirect-qr-code-workaround`}
        >
          <div className="bg-black text-white py-3 font-semibold uppercase text-center px-4 rounded-t-xl">
            {t(
              "Scan the QR code below with your mobile camera to be redirected to {{channel}} app",
              {
                channel: paymentChannel.display_name
              }
            )}
          </div>
          <div className="w-full h-80 border border-t-0 border-xen-gray-400 rounded-b-xl flex justify-center items-center p-1.5">
            <button onClick={handleQrCodeClick}>
              {redirectUrl ? <QrCode value={redirectUrl} ref={qrRef} /> : null}
            </button>
          </div>
        </div>

        <Button
          type="button"
          onClick={clearPaymentChannel}
          variant="brand-secondary"
          outline
          className="w-64"
          data-testid="back"
        >
          {t("Back")}
        </Button>
      </div>
    );
  }

  // Default redirect flow
  return (
    <div className="flex flex-col items-center py-12 px-4 md:px-0">
      <Graphic className="text-primary mb-4" />
      <p
        className="font-semibold text-center max-w-sm"
        data-testid="redirect-to-partner-copy"
      >
        {t("Redirecting you to {{channel}} checkout page in a moment…", {
          channel: paymentChannel.display_name
        })}
      </p>

      <Dialog
        open={!!error}
        title={error?.title}
        description={error?.body}
        buttons={[
          {
            text: "OK, Got it!",
            variant: "brand-secondary",
            onClick: () => {
              clearPaymentChannel();
            }
          }
        ]}
      />
    </div>
  );
};

export default PaymentRedirect;
